import React, { useEffect, useState } from 'react';
import APIService, { IActiveCountry, IActivityRecord } from 'services/APIService';
import { getErrorMessage } from 'utils/generalUtils';
import InsightDisplay from './InsightDisplay';
import { keyBy } from 'lodash/fp';
import rootState from 'RootStore';
import { C_DETAIL_VIEW_WAIT_FOR_ID_TIMEOUT_MS } from '../../../constants';

type Props = {
    id?: string;
    onInsightLoaded?: (insight: IActivityRecord | null) => void;
};

const InsightDisplayProvider = React.memo(({ id, onInsightLoaded }: Props) => {
    const [isLoadingInsight, setIsLoadingInsight] = useState(true);
    const [insightData, setInsightData] = useState<IActivityRecord>(null);
    const [error, setError] = useState<string>(null);
    const countriesById: { [countryID: number]: IActiveCountry } = keyBy(
        (country: IActiveCountry) => country.id,
        rootState.countryStore.countries.licensedCountriesArr
    );
    useEffect(() => {
        if (id) return void loadInsight(id);
        const timer = setTimeout(() => {
            setIsLoadingInsight(false);
            setError('No insight to display');
        }, C_DETAIL_VIEW_WAIT_FOR_ID_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [id]);
    const loadInsight = async (id: string) => {
        setIsLoadingInsight(true);
        setError(null);
        try {
            const insight = await APIService.getFullActivity(id);
            setInsightData(insight);
            onInsightLoaded?.(insight);
        } catch (err) {
            console.log('load insight error', err);
            setError(getErrorMessage(err, 'Failed to load insight'));
        } finally {
            setIsLoadingInsight(false);
        }
    };

    return (
        <InsightDisplay
            insight={insightData}
            isLoading={isLoadingInsight}
            error={error}
            countriesById={countriesById}
        />
    );
});

export default InsightDisplayProvider;
